import React from "react";

// Creating custom hook with context
const CreateHook = (name, context) => {
  const reactContext = React.useContext(context);
  if (!reactContext) {
    throw new Error(
      `${name} hook must be used within a ContextProvider component`
    );
  }
  return reactContext;
};

const shouldSendAnalytics = () => {
  const isLocalhost = () => {
    return (
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"
    );
  };
  return process.env.NODE_ENV === "production" && !isLocalhost();
};

// ----- converting values to bytes -----
const convertPercentToCommandValue = (percent) => {
  return Math.max(1, Math.round((percent / 100) * 255));
};

const convertValueToUint16 = (value) => {
  const lowByte = value & 0xff;
  const highByte = (value >> 8) & 0xff;
  return [highByte, lowByte];
};

const rgbStringToArray = (rgbString) => {
  // colors for LED has to be written in the following order: GREEN, BLUE, RED
  const rgbArray = rgbString.match(/\d+/g).map((value) => parseInt(value, 10));
  const firstElement = rgbArray.shift();
  rgbArray.push(firstElement);
  return [rgbArray[0], rgbArray[2], rgbArray[1]];
};

const getRGBarray = (rgbColor) =>
  rgbColor.match(/\d+/g).map((value) => parseInt(value, 10));

const mapHexColorsToNames = (hexColor) => {
  switch (hexColor.toUpperCase()) {
    case "#CC0000":
      return "Red";
    case "#CC8400":
      return "Orange";
    case "#CCCC00":
      return "Yellow";
    case "#00CC00":
      return "Green";
    case "#00CCCC":
      return "Cyan";
    case "#0000CC":
      return "Blue";
    case "#6600CC":
      return "Violet";
    case "#CC00CC":
      return "Magenta";
    case "#CC99A3":
      return "Pink";
    default:
      return hexColor.toUpperCase();
  }
};

const hexToRgb = (hex) => {
  if (!hex) {
    return;
  }
  // Remove the hash if present
  hex = hex.replace(/^#/, "");

  // Parse the hex values to RGB
  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return `rgb(${r},${g},${b})`;
};

/**
 * Converts a given rgb colorstring back to a hex colorstring.
 * @param {string} rgb Color in rgb string format (rgb(255, 255, 255))
 * @returns {string} Hex color (#FFFFFF)
 */
const rgbToHex = (rgb) => {
  if (!rgb) {
    return;
  }

  // Extract the RGB values from the string
  const result = rgb.match(/\d+/g);

  if (!result || result.length !== 3) {
    return;
  }

  // Convert the RGB values to hexadecimal
  const r = parseInt(result[0], 10).toString(16).padStart(2, "0");
  const g = parseInt(result[1], 10).toString(16).padStart(2, "0");
  const b = parseInt(result[2], 10).toString(16).padStart(2, "0");

  return `#${r}${g}${b}`;
};

const findMaxRGBValue = (rgbArray) => {
  let max = 0;
  // Loop through each RGB string in the array
  rgbArray.forEach((rgbString) => {
    // Extract the values of red, green, and blue from the RGB string
    const matches = rgbString.match(/rgb\((\d+),(\d+),(\d+)\)/);

    if (matches) {
      // Convert the extracted values to numbers
      const [_, red, green, blue] = matches.map(Number);
      // Update the max value if any of x, y, or z is greater
      max = Math.max(max, red, green, blue);
    }
  });

  return max;
};

// TODO remove secondary colors
// function to map colors from ColorList to colors according to Spot W1 BT docmenation
const mapRGBtoTableColors = (rgbColor) => {
  switch (rgbColor) {
    // red
    case "rgb(204,0,0)":
    case "rgb(64,0,0)":
      return "rgb(255,0,0)";
    // light orange
    case "rgb(204,132,0)":
    case "rgb(64,41,0)":
      return "rgb(255,144,0)";
    // yellow
    case "rgb(204,204,0)":
    case "rgb(64,64,0)":
      return "rgb(112,255,0)";
    // green
    case "rgb(60,148,18)":
    case "rgb(19,47,6)":
      return "rgb(0,255,0)";
    // cyan
    case "rgb(0,64,64)":
    case "rgb(0,204,204)":
      return "rgb(22,165,165)";
    // blue
    case "rgb(0,0,64)":
    case "rgb(0,0,204)":
      return "rgb(0,0,255)";
    // violet
    case "rgb(31,0,64)":
    case "rgb(102,0,204)":
      return "rgb(112,0,255)";
    // magenta
    case "rgb(64,0,64)":
    case "rgb(204,0,204)":
      return "rgb(204,0,204)";
    // pink
    case "rgb(64,48,51)":
    case "rgb(204,153,163)":
      return "rgb(254,59,169)";
    default:
      return rgbColor;
  }
};

export const matchRGBstringToColorId = (colorString) => {
  let colorId;
  switch (colorString) {
    case "rgb(64,0,0)":
    case "rgb(204,0,0)":
      colorId = 0; // red
      break;
    case "rgb(64,41,0)":
    case "rgb(204,132,0)":
      colorId = 5; // orange
      break;
    case "rgb(64,64,0)":
    case "rgb(204,204,0)":
      colorId = 7; // yellow
      break;
    case "rgb(19,47,6)":
    case "rgb(60,148,18)":
      colorId = 1; // green
      break;
    case "rgb(0,64,64)":
    case "rgb(0,204,204)":
      colorId = 9; // cyan
      break;
    case "rgb(0,0,64)":
    case "rgb(0,0,204)":
      colorId = 2; // blue
      break;
    case "rgb(31,0,64)":
    case "rgb(102,0,204)":
      colorId = 4; // violet
      break;
    case "rgb(64,0,64)":
    case "rgb(204,0,204)":
      colorId = 11; // magenta
      break;
    case "rgb(64,48,51)":
    case "rgb(204,153,163)":
      colorId = 10; // pink
      break;
  }

  return colorId;
};

export const getContainerWidth = (showTips) =>
  `calc(100vw - ${showTips ? "495px" : "260px"})`;

export function formatViewCount(count) {
  if (!count) {
    return "0";
  }

  if (count >= 1_000_000) {
    return (count / 1_000_000).toFixed(count % 1_000_000 === 0 ? 0 : 1) + "M";
  } else if (count >= 1_000) {
    return (count / 1_000).toFixed(count % 1_000 === 0 ? 0 : 1) + "K";
  } else {
    return count.toString();
  }
}

export const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export function buildParams(params) {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value.toString());
    }
  });
  return searchParams.toString();
}

export {
  CreateHook,
  shouldSendAnalytics,
  convertPercentToCommandValue,
  convertValueToUint16,
  rgbStringToArray,
  mapHexColorsToNames,
  hexToRgb,
  rgbToHex,
  findMaxRGBValue,
  mapRGBtoTableColors,
  getRGBarray,
};
